import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Provider from "./Provider";
import ThriveModal from "../../common/ThriveModal";
import ThriveCaseSelection from "../../common/ThriveCaseSelection";
import ThriveResourceEditor from "../../common/ThriveResourceEditor";
import { StyledButtonMain } from "../../common/Buttons";
import EmailEditor from "../../common/EmailEditor";
import { Provider15minsReferral } from "../../common/EmailBodies/Provider15minsReferral";
import NoticeDialog from "../../common/NoticeDialog";
import FactSheetsList from "../Resources/FactsheetsList";
import ResourceNotes from "../Resources/ResourceNotes";
import ProviderHis from "./ProviderHis";
import ProviderArchive from "./ProvidersArch";
import DirectoryLayout, { touchLinkRow } from "../Directory/DirectoryLayout";
import CaseReferralBanner from "../Directory/CaseReferralBanner";
import { useDirectoryList } from "../Directory/useDirectoryList";
import { axiosInstance } from "../../../utils/networking";
import { TOUCH_QUERY } from "../../../utils/useViewport";
import { useNavigate, useSearchParams } from "react-router-dom";
export interface providerData {
  _id: string;
  id: string;
  title: string;
  url: string;
  tags: string[];
  imagePath: string;
  orgName: string;
  address: string;
  category: string;
  description: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export interface tagsData {
  tag: string;
}

const categoryOf = (provider: providerData) => provider.category;

const byOrgName = (a: providerData, b: providerData) => {
  const nameA = a.orgName.toUpperCase();
  const nameB = b.orgName.toUpperCase();

  if (nameA < nameB) {
    return -1;
  }

  if (nameA > nameB) {
    return 1;
  }

  return 0;
};

// The server shuffles the providers on every request (TD-170), so that none
// is always listed first. Only the full-page reload after an edit, a note or
// a restore keeps the order it left, so the same providers come back on the
// same ?page=; every other visit takes the server's fresh order and saves it
// for such a reload. Without storage the server's order is used.
const ORDER_KEY = "thrive.providers.order";

// The first provider list of a page load is the one a reload came back to.
let firstListOfPageLoad = true;

const reloadedThisPage = () => {
  try {
    const [entry] = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    return (
      entry?.type === "reload" &&
      new URL(entry.name).pathname === window.location.pathname
    );
  } catch {
    return false;
  }
};

const inDisplayOrder = (providers: providerData[]) => {
  const reuseSavedOrder = firstListOfPageLoad && reloadedThisPage();
  firstListOfPageLoad = false;
  try {
    let ordered = providers;
    if (reuseSavedOrder) {
      const saved: unknown = JSON.parse(
        sessionStorage.getItem(ORDER_KEY) || "[]"
      );
      const rank = new Map(
        Array.isArray(saved)
          ? saved.map((id, index): [string, number] => [String(id), index])
          : []
      );
      ordered = [...providers].sort(
        (a, b) =>
          (rank.get(a._id) ?? rank.size) - (rank.get(b._id) ?? rank.size)
      );
    }
    sessionStorage.setItem(
      ORDER_KEY,
      JSON.stringify(ordered.map((provider) => provider._id))
    );
    return ordered;
  } catch {
    return providers;
  }
};

// Touch screens: an 18 px box and a 40 px tall label.
const touchCheckbox = {
  [`@media ${TOUCH_QUERY}`]: {
    "& input": { width: 18, height: 18, alignSelf: "center" },
    "& label": { display: "flex", alignItems: "center", minHeight: 40 },
  },
};

function ProviderList(props: any) {
  const [selectedProvider, setSelectedProvider] = useState<providerData[]>([]);
  const [availableProviders, setAvailableProviders] = useState<providerData[]>(
    []
  );

  //Edit Resource States
  const [resourceEditorOpen, setResourceEditorOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<any>({});

  const [editorOpen, setEditorOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [searchDescription, setSearchDescription] = useState<boolean>(false);

  const [displayCases, setDisplayCases] = useState<any>([]);
  const [selectedCase, setSelectedCase] = useState<any>({ firmRecord: {} });
  const [checkedCases, setCheckedCases] = useState<string[]>([]);
  const [cardOpen, setCardOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [dataFlag, setDataFlag] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<any>({});
  const [providerFactsheetOpen, setProviderFactsheetOpen] = useState(false);
  const [providerNotesOpen, setProviderNotesOpen] = useState(false);
  const [hisOpen, setHisOpen] = useState(false);
  const [hisProvider, setHisProvider] = useState<any>({});
  const [archiveOpen, setArchiveOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkCaseId = searchParams.get("case_id");
  //"Sort" (A-Z) is ?sort=name next to ?page=, so the reloads keep it too
  const sortByName = searchParams.get("sort") === "name";
  const handleSortClick = () => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("sort", "name");
        return next;
      },
      { replace: true }
    );
  };
  //activities keyed by case_id, used by the Business Needs Assessment check
  const [caseActivityCache, setCaseActivityCache] = useState<any>({});
  const [caseLookupFailed, setCaseLookupFailed] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeTitle, setNoticeTitle] = useState("");
  const [noticeBody, setNoticeBody] = useState("");

  const showNotice = useCallback((title: string, body: string) => {
    setNoticeTitle(title);
    setNoticeBody(body);
    setNoticeOpen(true);
  }, []);

  //keyed on the case id alone, so paging (?page=) does not load it again
  useEffect(() => {
    if (!deepLinkCaseId) {
      return;
    }
    axiosInstance
      .get(`/cases?id=${deepLinkCaseId}`)
      .then((res) => {
        if (res.status === 200 && res.data && res.data.case) {
          setCaseLookupFailed(false);
          setSelectedCase(res.data.case);
          setSelectedFirm(res.data.case.firmRecord);
        } else {
          setCaseLookupFailed(true);
          showNotice(
            "Client Not Found",
            "We could not load the client for this referral link. Please go back to the case and try again."
          );
        }
      })
      .catch(() => {
        setCaseLookupFailed(true);
        showNotice(
          "Client Not Found",
          "We could not load the client for this referral link. Please go back to the case and try again."
        );
      });
    //pre-load this case's activities so the Business Needs Assessment check
    //is instant when Connect is clicked
    axiosInstance
      .get(`/activity?case_id=${deepLinkCaseId}`)
      .then((res) => {
        setCaseActivityCache((prev: any) => ({
          ...prev,
          [deepLinkCaseId]: res.data.activity || [],
        }));
      })
      .catch(() => {});
  }, [deepLinkCaseId, showNotice]);

  useEffect(() => {
    //only the last request counts (development mounts effects twice)
    let current = true;
    axiosInstance.get(`/providers`).then((res) => {
      if (current) {
        setAvailableProviders(inDisplayOrder(res.data.providers));
      }
    });
    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    axiosInstance.get(`/tags/provider`).then((res) => {
      setAvailableTags(res.data.tags);
    });
  }, []);

  //the same fields with and without "Include Description", which adds the
  //description
  const matchesSearch = useCallback(
    (provider: providerData, query: string) =>
      [
        provider.orgName,
        provider.firstName,
        provider.lastName,
        provider.phone,
        provider.email,
        searchDescription && provider.description,
      ].some(
        (field) => typeof field === "string" && field.toLowerCase().includes(query)
      ),
    [searchDescription]
  );

  const list = useDirectoryList({
    items: availableProviders,
    availableTags,
    categoryOf,
    matchesSearch,
    compare: sortByName ? byOrgName : undefined,
    resetKeys: [searchDescription],
  });

  //Shared Business Needs Assessment gate - used by the case selection modal
  //(handleNext) and by Connect with a case already chosen (handleConnectClick)
  const hasBusinessAssessment = (caseId: string, activities: any) => {
    if (!activities) {
      return false;
    }
    const theActivities = activities.filter((item: any) => {
      return item.case_id === caseId;
    });
    const BNAActivity = theActivities.find((item: any) => {
      return item.activity_type === "Business Assessment";
    });
    return Boolean(BNAActivity);
  };

  const BNA_MISSING_MESSAGE =
    "Selected client does not have a completed Business Needs Assessment. Please complete the assessment before referring your client to a B2B Provider.";

  //this case's activities, loaded once per case; null when the lookup fails
  const loadCaseActivities = async (caseId: string) => {
    if (caseActivityCache[caseId]) {
      return caseActivityCache[caseId];
    }
    try {
      const res = await axiosInstance.get(`/activity?case_id=${caseId}`);
      const activities = res.data.activity || [];
      setCaseActivityCache((prev: any) => ({
        ...prev,
        [caseId]: activities,
      }));
      return activities;
    } catch (err) {
      return null;
    }
  };

  //opens the referral email when the case has a Business Needs Assessment,
  //otherwise explains why not
  const openEditorIfAssessed = async (caseId: string) => {
    const activities = await loadCaseActivities(caseId);
    if (activities === null) {
      showNotice(
        "Unable to Verify Assessment",
        "We could not check whether this client has a completed Business Needs Assessment. Please try again."
      );
      return false;
    }
    if (!hasBusinessAssessment(caseId, activities)) {
      showNotice("Business Needs Assessment Required", BNA_MISSING_MESSAGE);
      return false;
    }
    setEditorOpen(true);
    return true;
  };

  const handleConnectClick = async () => {
    if (selectedCase && selectedCase._id) {
      await openEditorIfAssessed(selectedCase._id);
    } else {
      setCardOpen(true);
    }
  };

  const handleAddNewProvider = () => {
    setEditingResource({});
    setResourceEditorOpen(true);
  };

  const handleExport = () => {
    //Export to xlsx file
    const XLSX = require("xlsx");
    //every provider, A-Z after Sort
    const providersToExport = sortByName
      ? [...availableProviders].sort(byOrgName)
      : availableProviders;
    //only export orgName, firstName, lastName, phone, email
    const exportProviders = providersToExport.map((provider) => {
      return {
        orgName: provider.orgName,
        firstName: provider.firstName,
        lastName: provider.lastName,
        phone: provider.phone,
        email: provider.email,
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportProviders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Providers");
    XLSX.writeFile(wb, "Providers.xlsx");

    axiosInstance.post("/export_log", {
      export_type: "providers",
      record_count: availableProviders.length,
      filters: {},
    });
  };

  return (
    <div className="">
      <DirectoryLayout
        list={list}
        itemNoun="provider"
        banner={
          deepLinkCaseId && (
            <CaseReferralBanner
              failed={caseLookupFailed}
              companyName={selectedFirm["Company Name"]}
              hint="Select providers below, then click Connect to case to send this client a B2B referral."
              onBack={() => navigate(`/case?id=${deepLinkCaseId}`)}
            />
          )
        }
        searchLabel="Search Providers"
        searchExtras={
          <Box className="flex justify-end" sx={touchCheckbox}>
            <input
              type="checkbox"
              id="search_description"
              name="search_description"
              checked={searchDescription}
              onChange={(event) => setSearchDescription(event.target.checked)}
              className="accent-magenta"
            />
            <label htmlFor="search_description" className="text-left ml-2">
              Include Description
            </label>
          </Box>
        }
        adminActions={
          props.user.role === "admin" && (
            <>
              <StyledButtonMain
                variant="contained"
                onClick={handleAddNewProvider}
              >
                Add new Provider
              </StyledButtonMain>

              <Stack
                direction={"row"}
                spacing={2}
                className="flex justify-end"
                sx={touchLinkRow}
              >
                <button
                  className="text-magenta underline"
                  onClick={() => setArchiveOpen(true)}
                >
                  Archive
                </button>
                <button
                  className="text-magenta underline"
                  onClick={handleSortClick}
                >
                  Sort
                </button>
                <button
                  className="text-magenta underline"
                  onClick={handleExport}
                >
                  Export
                </button>
              </Stack>
            </>
          )
        }
        cardSpacing={3}
        renderItem={(provider) => (
          <Provider
            key={provider._id || provider.orgName}
            tagDisplay={true}
            handleTagClick={list.showOnlyTag}
            providerContent={provider}
            selectedProvider={selectedProvider}
            setSelectedProvider={setSelectedProvider}
            setResourceEditorOpen={setResourceEditorOpen}
            setEditingResource={setEditingResource}
            setProviderFactsheetOpen={setProviderFactsheetOpen}
            setProviderNotesOpen={setProviderNotesOpen}
            setHisOpen={setHisOpen}
            setHisProvider={setHisProvider}
          />
        )}
        //selected providers may sit on other pages, so Connect lives here
        selection={{
          count: selectedProvider.length,
          actionLabel: "Connect to case",
          onAction: handleConnectClick,
          onClear: () => setSelectedProvider([]),
        }}
      />
      {/* Select Case Popup Box*/}
      <ThriveModal
        open={cardOpen}
        setOpen={setCardOpen}
        contentComponent={
          <ThriveCaseSelection
            height={450}
            cases={[]}
            reloadCases={true}
            checkedCases={checkedCases}
            setCheckedCases={setCheckedCases}
            selectedCase={selectedCase}
            setSelectedCase={setSelectedCase}
            enableControl={true}
            enableSelection={true}
            enableMultiSelection={false}
            setOpen={setCardOpen}
            status_default="either"
            sortby_default="recent"
            period_default="all"
            title={"Select Connecting Case"}
            handleNext={async () => {
              if (!selectedCase || !selectedCase._id) {
                return;
              }
              const res = await axiosInstance.get(
                `/firms/id?id=${selectedCase["firmRecord"]["_id"]}`
              );
              setSelectedFirm(res.data.firm);

              if (await openEditorIfAssessed(selectedCase["_id"])) {
                setCardOpen(false);
              }
            }}
          ></ThriveCaseSelection>
        }
      ></ThriveModal>
      <ThriveModal
        height={750}
        open={editorOpen}
        setOpen={setEditorOpen}
        contentComponent={
          <EmailEditor
            setOpen={setEditorOpen}
            theCase={selectedCase}
            theFirm={selectedFirm}
            theReferral={{}}
            theSubject={
              "Oakland Thrive - Providers List for " +
              selectedFirm["Company Name"]
            }
            editorTitle={"Providers Referral Email"}
            theSelectedEvent={{}}
            bodyGenerator={Provider15minsReferral}
            setMessageOpen={setMessageOpen}
            setMessageTitle={setMessageTitle}
            setMessageBody={setMessageBody}
            checkedCases={checkedCases}
            displayCases={displayCases}
            theSelectedProvider={selectedProvider}
            theBFC={{}}
          ></EmailEditor>
        }
      ></ThriveModal>
      <ThriveModal
        open={messageOpen}
        setOpen={setMessageOpen}
        closeByClick={true}
        height={200}
        width={500}
        contentComponent={
          <NoticeDialog
            title={"Email Sent!"}
            setOpen={setMessageOpen}
            setDataFlag={setDataFlag}
          ></NoticeDialog>
        }
      ></ThriveModal>
      {/* Notice popup: case lookup failures and the assessment check. Its
          height follows the text, whose title can take two lines. */}
      <ThriveModal
        open={noticeOpen}
        setOpen={setNoticeOpen}
        closeByClick={true}
        height="auto"
        width={560}
        contentComponent={
          <NoticeDialog
            title={noticeTitle}
            body={noticeBody}
            setOpen={setNoticeOpen}
            setDataFlag={setDataFlag}
            dataFlag={dataFlag}
          ></NoticeDialog>
        }
      ></ThriveModal>
      <ThriveModal
        open={resourceEditorOpen}
        setOpen={setResourceEditorOpen}
        contentComponent={
          <ThriveResourceEditor
            setOpen={setResourceEditorOpen}
            editorType={"Provider"}
            title={"Provider Editor"}
            editingResource={editingResource}
          ></ThriveResourceEditor>
        }
      ></ThriveModal>

      {/* modal for provider factsheets */}
      <ThriveModal
        open={providerFactsheetOpen}
        setOpen={setProviderFactsheetOpen}
        contentComponent={
          <FactSheetsList
            setOpen={setProviderFactsheetOpen}
            resource={editingResource}
          ></FactSheetsList>
        }
      ></ThriveModal>

      {/* modal for resource notes */}
      <ThriveModal
        open={providerNotesOpen}
        setOpen={setProviderNotesOpen}
        contentComponent={
          <ResourceNotes
            setOpen={setProviderNotesOpen}
            resource={editingResource}
            editorType={"Provider"}
          ></ResourceNotes>
        }
      ></ThriveModal>

      {/* modal for provider his */}
      <ThriveModal
        open={hisOpen}
        setOpen={setHisOpen}
        contentComponent={
          <ProviderHis
            setOpen={setHisOpen}
            hisProvider={hisProvider}
          ></ProviderHis>
        }
      ></ThriveModal>

      <ThriveModal
        open={archiveOpen}
        setOpen={setArchiveOpen}
        contentComponent={
          <ProviderArchive open={archiveOpen} setOpen={setArchiveOpen} />
        }
      ></ThriveModal>
    </div>
  );
}

export default ProviderList;
